import React, { useEffect, useState, useMemo } from 'react';
import { useTable, usePagination } from 'react-table';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

function AdminPanel() {
    const navigate = useNavigate();
    const [bookings, setBookings] = useState([]);
    const [feedbacks, setFeedbacks] = useState([]);
    const [bookingFilter, setBookingFilter] = useState('');
    const [feedbackFilter, setFeedbackFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [citySort, setCitySort] = useState('');
    const [ratingSort, setRatingSort] = useState('');

    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (!token) return navigate('/admin-login');
        fetchData(token);
    }, [navigate]);

    // ✅ API Base URL (local vs production)
    const API_BASE_URL =
        window.location.hostname === "localhost"
            ? "http://localhost:5000"
            : "https://rapidphysio-backend-ka9x.onrender.com";

    // ✅ Common function: delete ALL data
    const deleteAllData = async (endpoint, setState, itemName) => {
        if (window.confirm(`Are you sure you want to delete ALL ${itemName}?`)) {
            try {
                const token = localStorage.getItem("adminToken");
                await fetch(`${API_BASE_URL}/api/admin/${endpoint}`, {
                    method: "DELETE",
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });
                setState([]); // clear frontend state
                alert(`✅ All ${itemName} deleted successfully!`);
            } catch (error) {
                console.error(`Failed to delete all ${itemName}`, error);
                alert(`❌ Failed to delete all ${itemName}`);
            }
        }
    };

    // ✅ Common function: delete SINGLE data
    const deleteSingleData = async (endpoint, id, setState, itemName) => {
        if (window.confirm(`Are you sure you want to delete this ${itemName}?`)) {
            try {
                const token = localStorage.getItem("adminToken");
                await fetch(`${API_BASE_URL}/api/admin/${endpoint}/${id}`, {
                    method: "DELETE",
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });
                setState(prev => prev.filter(item => item._id !== id)); // remove from UI
                alert(`✅ ${itemName} deleted successfully!`);
            } catch (error) {
                console.error(`Failed to delete ${itemName}`, error);
                alert(`❌ Failed to delete ${itemName}`);
            }
        }
    };

    const fetchData = async (token) => {
        try {
            const [res1, res2] = await Promise.all([
                fetch(`${API_BASE_URL}/api/admin/bookings`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
                fetch(`${API_BASE_URL}/api/admin/feedbacks`, {
                    headers: { Authorization: `Bearer ${token}` },
                })
            ]);
            const data1 = await res1.json();
            const data2 = await res2.json();
            setBookings(data1);
            setFeedbacks(data2);
        } catch (err) {
            console.error('Fetch error:', err);
            navigate('/admin-login');
        }
    };

    const logout = () => {
        localStorage.removeItem('adminToken');
        navigate('/admin-login');
    };

    const exportBookingsExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(bookings);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Bookings');
        XLSX.writeFile(workbook, 'bookings.xlsx');
    };

    const exportBookingsPDF = () => {
        const doc = new jsPDF();

        doc.setFontSize(16);
        doc.text("Bookings Report", 14, 15);

        autoTable(doc, {
            startY: 20,
            head: [['Name', 'Email', 'Phone', 'City', 'Date', 'Time', 'Therapy Type', 'Therapy Mode']],
            body: bookings.map(b => [
                b.name,
                b.email,
                b.phone,
                b.city,
                b.date,
                b.time,
                b.therapyType,
                b.therapyMode
            ]),
            styles: {
                fontSize: 10,
                cellPadding: 3
            },
            headStyles: {
                fillColor: [22, 160, 133],
                textColor: 255,
                halign: 'center'
            },
            theme: 'striped'
        });

        doc.save('bookings.pdf');
    };


    const highlightMatch = (text, query) => {
        if (!query) return text;
        const regex = new RegExp(`(${query})`, 'gi');
        return text.replace(regex, '<mark>$1</mark>');
    };

    const searchStyle = {
        width: '180px',
        padding: '6px 8px',
        fontSize: '14px',
        marginBottom: '10px',
        borderRadius: '4px',
        border: '1px solid #ccc'
    };


    const filteredBookings = useMemo(() => {
        let filtered = bookings.filter(item => {
            return ['city', 'date', 'time', 'therapyType', 'therapyMode'].some(field =>
                String(item[field] || '').toLowerCase().includes(bookingFilter.toLowerCase())
            );
        });
        if (startDate && endDate) {
            filtered = filtered.filter(b => new Date(b.date) >= new Date(startDate) && new Date(b.date) <= new Date(endDate));
        }
        if (citySort) {
            filtered = filtered.filter(b => b.city === citySort);
        }
        return filtered;
    }, [bookings, bookingFilter, startDate, endDate, citySort]);

    const filteredFeedbacks = useMemo(() => {
        let filtered = feedbacks.filter(item => String(item.rating).includes(feedbackFilter));
        if (ratingSort) {
            filtered = filtered.filter(f => f.rating === parseInt(ratingSort));
        }
        return filtered;
    }, [feedbacks, feedbackFilter, ratingSort]);

    return (
        <div style={styles.container}>
            <div style={styles.headerRow}>
                <h2>🛠️ Admin Panel</h2>
                <button style={styles.btnPrimary} onClick={logout}>Logout</button>
            </div>

            {/* Bookings Section */}
            <section style={styles.card}>
                <div style={styles.cardHeader}>
                    <h3>📅 Bookings</h3>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                        {/* <button style={styles.btnDanger} onClick={() => setBookings([])}>Delete All</button> */}
                        <button
                            style={styles.btnDanger}
                            onClick={() => deleteAllData("bookings", setBookings, "bookings")}
                        >
                            Delete All
                        </button>
                        <button style={styles.btnSecondary} onClick={exportBookingsExcel}>Export Excel</button>
                        <button style={styles.btnSecondary} onClick={exportBookingsPDF}>Export PDF</button>
                    </div>
                </div>

                <div style={styles.filtersRow}>
                    <input type="text" value={bookingFilter} onChange={e => setBookingFilter(e.target.value)} placeholder="Search bookings..." style={searchStyle} />
                    <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={styles.dateInput} />
                    <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={styles.dateInput} />
                    <select value={citySort} onChange={e => setCitySort(e.target.value)} style={styles.dropdown}>
                        <option value="">Sort by City</option>
                        {[...new Set(bookings.map(b => b.city))].map(city => <option key={city}>{city}</option>)}
                    </select>
                </div>

                <div style={styles.tableWrapper}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th>Name</th><th>Email</th><th>Phone</th><th>City</th><th>Date</th><th>Time</th><th>Therapy Type</th><th>Therapy Mode</th><th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredBookings.map((b, i) => (
                                <tr key={i}>
                                    <td dangerouslySetInnerHTML={{ __html: highlightMatch(b.name, bookingFilter) }} />
                                    <td>{b.email}</td>
                                    <td>{b.phone}</td>
                                    <td>{b.city}</td>
                                    <td>{b.date}</td>
                                    <td>{b.time}</td>
                                    <td>{b.therapyType}</td>
                                    <td>{b.therapyMode}</td>
                                    <td>
                                        <button
                                            style={styles.btnDanger}
                                            onClick={() => deleteSingleData("bookings", b._id, setBookings, "booking")}
                                        >
                                            Delete
                                        </button>
                                    </td>

                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p style={styles.countText}>Total Bookings: {filteredBookings.length}</p>
            </section>

            {/* Feedback Section */}
            <section style={styles.card}>
                <div style={styles.cardHeader}>
                    <h3>⭐ Feedbacks</h3>
                    <div>
                        {/* <button style={styles.btnDanger} onClick={() => setFeedbacks([])}>Delete All</button> */}
                        <button
                            style={styles.btnDanger}
                            onClick={() => deleteAllData("feedbacks", setFeedbacks, "feedbacks")}
                        >
                            Delete All
                        </button>

                    </div>
                </div>

                <div style={styles.filtersRow}>
                    <input type="text" value={feedbackFilter} onChange={e => setFeedbackFilter(e.target.value)} placeholder="Search by rating..." style={searchStyle} />
                    <select value={ratingSort} onChange={e => setRatingSort(e.target.value)} style={styles.dropdown}>
                        <option value="">Sort by Rating</option>
                        {[1, 2, 3, 4, 5].map(r => <option key={r}>{r}</option>)}
                    </select>
                </div>

                <div style={styles.tableWrapper}>
                    <table style={styles.table}>
                        <thead>
                            <tr><th>Name</th><th>Feedback</th><th>Rating</th><th>Actions</th></tr>
                        </thead>
                        <tbody>
                            {filteredFeedbacks.map((f, i) => (
                                <tr key={i}>
                                    <td>{f.name}</td>
                                    <td>{f.feedback}</td>
                                    <td>{'⭐'.repeat(f.rating)}</td>
                                    <td>
                                        <button
                                            style={styles.btnDanger}
                                            onClick={() => deleteSingleData("feedbacks", f._id, setFeedbacks, "feedback")}
                                        >
                                            Delete
                                        </button>
                                    </td>

                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p style={styles.countText}>Total Feedbacks: {filteredFeedbacks.length}</p>
            </section>
        </div>
    );
}

const styles = {
    container: {
        padding: '20px',
        fontFamily: 'Arial',
        backgroundColor: '#f5f5f5',
        minHeight: '100vh'
    },
    headerRow: {
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        alignItems: 'center',
        marginBottom: '20px'
    },
    btnPrimary: {
        backgroundColor: '#4CAF50',
        color: 'white',
        padding: '8px 12px',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer'
    },
    btnSecondary: {
        backgroundColor: '#2196F3',
        color: 'white',
        padding: '8px 12px',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer'
    },
    btnDanger: {
        backgroundColor: '#f44336',
        color: 'white',
        padding: '6px 10px',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer'
    },
    card: {
        backgroundColor: 'white',
        borderRadius: '10px',
        padding: '15px',
        marginBottom: '25px',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        marginBottom: '15px'
    },
    filtersRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
        marginBottom: '15px'
    },
    searchBox: {
        padding: '6px 10px',
        borderRadius: '5px',
        border: '1px solid #ccc',
        flex: '1 1 200px'
    },
    dateInput: {
        padding: '6px 10px',
        borderRadius: '5px',
        border: '1px solid #ccc'
    },
    dropdown: {
        padding: '6px 10px',
        borderRadius: '5px',
        border: '1px solid #ccc'
    },
    tableWrapper: {
        overflowX: 'auto'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse'
    },
    countText: {
        textAlign: 'right',
        fontWeight: 'bold',
        marginTop: '10px'
    }
};

export default AdminPanel;
